import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Shield, Calculator } from "lucide-react";

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-100 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-12 gap-10">
        {/* Brand + address */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded bg-white text-slate-900">
              <Shield className="w-5 h-5" />
            </span>
            <div>
              <div className="font-black tracking-tight">ALOKA</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Corporate · Legal · Finance</div>
            </div>
          </div>
          <p className="text-slate-400 text-sm max-w-md leading-relaxed mb-5">
            India + USA consulting marketplace. Tax, GST, ROC, Incorporation, Loans and US LLC/C-Corp/S-Corp.
            Verified CAs, transparent pricing, 15% platform fee, satisfaction-guaranteed refunds.
          </p>
          <div className="text-sm text-slate-300 space-y-2">
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-slate-400" /><span>625, Suraksha Smart City, Madhuban Township, Rajavali, Golani Naka, Vasai East, Vasai-Virar, Maharashtra 401208</span></div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /><a href="tel:+918652422980" data-testid="footer-phone">+91 86524 22980</a></div>
            <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><a href="mailto:aloka.co.in@gmail.com" data-testid="footer-email">aloka.co.in@gmail.com</a></div>
          </div>
        </div>

        {/* Calculators */}
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-400 font-bold mb-4 flex items-center gap-1"><Calculator className="w-3.5 h-3.5"/>Calculators</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-emi">EMI Calculator</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-fd">FD Calculator</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-rd">RD Calculator</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-sip">SIP Calculator</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-lumpsum">Lumpsum Calculator</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-tax">Income Tax (Old vs New)</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-hra">HRA Exemption</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-gst">GST Calculator</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-nps">NPS Pension</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-cg">Capital Gains</Link></li>
            <li><Link to="/tools" className="hover:text-white" data-testid="footer-le">Loan Eligibility</Link></li>
            <li><Link to="/tools" className="hover:text-white">All Tools →</Link></li>
          </ul>
        </div>

        {/* Tax & Compliance links */}
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-400 font-bold mb-4">Tax & Compliance</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><a href="https://www.incometax.gov.in/iec/foportal/" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="link-itr">Income Tax e-Filing</a></li>
            <li><a href="https://www.tdscpc.gov.in/" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="link-traces">TRACES</a></li>
            <li><a href="https://www.gst.gov.in/" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="link-gst">GST Portal</a></li>
            <li><a href="https://einvoice.gst.gov.in/" target="_blank" rel="noreferrer" className="hover:text-white">e-Invoice</a></li>
            <li><a href="https://ewaybillgst.gov.in/" target="_blank" rel="noreferrer" className="hover:text-white">E-Way Bill</a></li>
            <li><a href="https://www.protean-tinpan.com/" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="link-nsdl">NSDL / Protean</a></li>
            <li><a href="https://tin.tin.nsdl.com/tan/" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="link-tan">TAN Apply / Track</a></li>
          </ul>
        </div>

        {/* Labour / MCA */}
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-400 font-bold mb-4">PF · ESIC · MCA</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><a href="https://unifiedportal-mem.epfindia.gov.in/memberinterface/" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="link-epfo">EPFO Member</a></li>
            <li><a href="https://unifiedportal-emp.epfindia.gov.in/epfo/" target="_blank" rel="noreferrer" className="hover:text-white">EPFO Employer</a></li>
            <li><a href="https://passbook.epfindia.gov.in/" target="_blank" rel="noreferrer" className="hover:text-white">UAN Passbook</a></li>
            <li><a href="https://www.esic.gov.in/" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="link-esic">ESIC Portal</a></li>
            <li><a href="https://mlwb.maharashtra.gov.in/" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="link-mlwf">MLWF</a></li>
            <li><a href="https://www.mca.gov.in/" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="link-mca">MCA Portal</a></li>
            <li><a href="https://udyamregistration.gov.in/" target="_blank" rel="noreferrer" className="hover:text-white">Udyam / MSME</a></li>
          </ul>
        </div>

        {/* Aloka */}
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-400 font-bold mb-4">Aloka</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/services" className="hover:text-white">Services</Link></li>
            <li><Link to="/tools" className="hover:text-white">Tools</Link></li>
            <li><Link to="/community" className="hover:text-white">Community</Link></li>
            <li><Link to="/debate" className="hover:text-white">AI Debates</Link></li>
            <li><Link to="/about" className="hover:text-white">About</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Aloka Corporate & Legal Solutions. All rights reserved.
      </div>
    </footer>
  );
}
